// FCP design system — MoreMenu primitive (v2.8.34)
//
// Kebab button + popover menu, used inline on cards and rows to expose
// per-record actions without taking action-row space at the bottom of the
// card. The pattern is: card is clickable → opens View Details; kebab
// menu in top-right exposes Edit, Delete, and other less-common actions.
//
// Compound API:
//   <MoreMenu label="Job actions" align="right">
//     <MoreMenu.Item icon="eye" onClick={...}>View Details</MoreMenu.Item>
//     <MoreMenu.Item icon="edit" onClick={...}>Edit</MoreMenu.Item>
//     <MoreMenu.Separator />
//     <MoreMenu.Item icon="trash" tone="danger" onClick={...}>Delete</MoreMenu.Item>
//   </MoreMenu>
//
// Behavior:
//   - Click kebab → menu opens, anchored to button. First item focused.
//   - Esc, click-outside, or selecting an item → menu closes.
//   - Arrow keys cycle focus through items (Tab also moves linearly).
//   - All clicks inside this component stopPropagation, so a parent
//     card's onClick handler does NOT fire when the user interacts
//     with the menu (this is the critical UX fix for the
//     click-card-to-view + kebab-for-actions pattern).
//   - `align="right"` (default) positions the popover's right edge
//     under the button. `align="left"` does the opposite.
//   - `tone="danger"` on an Item gives it red treatment for destructive
//     actions like Delete.
//
// Why a custom popover (not a third-party menu lib): the design system
// is dependency-light and we have full control over the visual treatment
// + token-based theming. The implementation is ~150 lines.

import React, { useState, useRef, useEffect, useCallback, Children, isValidElement } from 'react';
import { Icon } from './Icon.js';
import './MoreMenu.css';

function MoreMenu({ children, label = 'More actions', align = 'right', className = '' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close on click outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Close on Esc, focus first item on open.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    // Focus first focusable item.
    requestAnimationFrame(() => {
      const first = menuRef.current?.querySelector('[role="menuitem"]:not([disabled])');
      first?.focus();
    });
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Arrow-key navigation inside the menu.
  const onMenuKeyDown = useCallback((e) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || []);
    if (items.length === 0) return;
    const currentIdx = items.indexOf(document.activeElement);
    let nextIdx;
    if (e.key === 'ArrowDown')  nextIdx = currentIdx < items.length - 1 ? currentIdx + 1 : 0;
    else if (e.key === 'ArrowUp') nextIdx = currentIdx > 0 ? currentIdx - 1 : items.length - 1;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End')  nextIdx = items.length - 1;
    items[nextIdx]?.focus();
  }, []);

  // Wrap each MoreMenu.Item to inject the close behavior.
  const wrappedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    if (child.type === MoreMenuItem) {
      return React.cloneElement(child, {
        onSelect: () => setOpen(false),
      });
    }
    return child;
  });

  // stopPropagation on the wrapper so card-level click handlers don't fire.
  const stop = (e) => e.stopPropagation();

  return (
    <div className={`fcp-more ${className}`} onClick={stop} onKeyDown={stop}>
      <button
        ref={triggerRef}
        type="button"
        className={`fcp-more__trigger${open ? ' fcp-more__trigger--open' : ''}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
      >
        <Icon name="more" />
      </button>
      {open && (
        <div
          ref={menuRef}
          className={`fcp-more__menu fcp-more__menu--align-${align}`}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
        >
          {wrappedChildren}
        </div>
      )}
    </div>
  );
}

function MoreMenuItem({ children, icon, onClick, onSelect, tone = 'default', disabled = false }) {
  const handleClick = (e) => {
    e.stopPropagation();
    // Belt-and-braces, and unreachable today (#300 §2): the `disabled`
    // attribute on the button below is what actually enforces this — React
    // does not invoke a handler on a disabled form control, so deleting this
    // line reddens nothing.
    //
    // Kept, and said out loud, because deleting it and deleting the protection
    // look identical in a diff. The day somebody swaps `disabled` for a
    // "looks greyed out" class — which is a normal thing to do to a menu item
    // — this line is the only thing left, and its absence would be invisible.
    if (disabled) return;
    onClick?.(e);
    onSelect?.();
  };
  return (
    <button
      type="button"
      role="menuitem"
      className={`fcp-more__item fcp-more__item--${tone}`}
      onClick={handleClick}
      disabled={disabled}
    >
      {icon && <Icon name={icon} size={16} className="fcp-more__item-icon" />}
      <span className="fcp-more__item-label">{children}</span>
    </button>
  );
}

function MoreMenuSeparator() {
  return <div className="fcp-more__separator" role="separator" />;
}

MoreMenu.Item = MoreMenuItem;
MoreMenu.Separator = MoreMenuSeparator;

export { MoreMenu };
export default MoreMenu;
